// src/contexts/StreamingChatContext.tsx
// Shared streaming state that survives navigation between chat pages.
// Wraps all chat routes so that an SSE connection opened on ChatHome
// continues streaming into ChatThreadPage after the router navigates.

import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { v2ChatApi } from '@/services/v2-chat-api';
import type { V2StreamEvent, MessageSegment, ArtifactInfo, ToolSegmentPayload } from '@/types/chat';

// ─── Public interface ──────────────────────────────────────────────

export interface StreamingState {
  isStreaming: boolean;
  /**
   * True while a stop request is in-flight to the backend. Used to hard-block
   * the message input (decision 6) from when the user clicks Stop until the
   * backend confirms the agent loop has actually stopped.
   */
  isAborting: boolean;
  /**
   * Stop reason from the last completed stream. Populated on the `complete`
   * SSE event; `'aborted'` lets the UI render a live "Stopped" indicator on
   * the in-memory assistant message before the thread refetch lands.
   */
  lastStopReason: string | null;
  liveSegments: MessageSegment[];
  thinkingText: string;
  latestArtifact: ArtifactInfo | null;
  error: string | null;
}

interface StreamingChatContextValue extends StreamingState {
  startChat: (message: string, uploadIds?: string[], agentId?: string, modelOverride?: string, enableCacheDiagnostics?: boolean) => Promise<void>;
  sendMessage: (threadId: string, message: string, uploadIds?: string[]) => Promise<void>;
  /** Client-side abort only — does NOT stop the backend run. Prefer `abortStream`. */
  abort: () => void;
  /**
   * Stop the running agent run on the backend. Blocks until the backend has
   * acknowledged that the loop has finalized, then flips `isStreaming` and
   * `isAborting` to false. Safe to call only when `isStreaming` is true.
   */
  abortStream: (threadId: string) => Promise<void>;
  reset: () => void;
}

const INITIAL_STATE: StreamingState = {
  isStreaming: false,
  isAborting: false,
  lastStopReason: null,
  liveSegments: [],
  thinkingText: '',
  latestArtifact: null,
  error: null,
};

const StreamingChatContext = createContext<StreamingChatContextValue | null>(null);

export const useStreamingChat = (): StreamingChatContextValue => {
  const ctx = useContext(StreamingChatContext);
  if (!ctx) {
    throw new Error('useStreamingChat must be used within a StreamingChatProvider');
  }
  return ctx;
};

// ─── Provider ──────────────────────────────────────────────────────

interface StreamingChatProviderProps {
  children: React.ReactNode;
  onComplete?: () => void;
}

export const StreamingChatProvider: React.FC<StreamingChatProviderProps> = ({
  children,
  onComplete,
}) => {
  const [state, setState] = useState<StreamingState>(INITIAL_STATE);
  const abortRef = useRef<AbortController | null>(null);
  const navigate = useNavigate();

  const onCompleteRef = useRef(onComplete);
  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  const currentTextIndexRef = useRef<number | null>(null);

  const handleEvent = useCallback((event: V2StreamEvent) => {
    switch (event.type) {
      case 'thread_created':
        navigate(`/chat/${event.threadId as string}`);
        break;

      case 'turn_start':
        setState(prev => ({ ...prev, thinkingText: '' }));
        break;

      case 'text_segment_start':
        setState(prev => {
          const next = [...prev.liveSegments, { type: 'text' as const, text: '' }];
          currentTextIndexRef.current = next.length - 1;
          return { ...prev, liveSegments: next };
        });
        break;

      case 'thinking_delta':
        setState(prev => ({
          ...prev,
          thinkingText: prev.thinkingText + (event.delta as string),
        }));
        break;

      case 'tool_start':
        currentTextIndexRef.current = null;
        setState(prev => ({
          ...prev,
          liveSegments: [
            ...prev.liveSegments,
            {
              type: 'tool' as const,
              toolName: event.toolName as string,
              toolCallId: event.toolCallId as string,
              status: 'running' as const,
              latencyMs: 0,
            },
          ],
        }));
        break;

      case 'tool_result': {
        const eventPayload = event.payload as ToolSegmentPayload | undefined;
        setState(prev => ({
          ...prev,
          liveSegments: prev.liveSegments.map(seg =>
            seg.type === 'tool' && seg.toolCallId === event.toolCallId
              ? {
                  ...seg,
                  status: (event.success ? 'completed' : 'failed') as 'completed' | 'failed',
                  ...(eventPayload ? { payload: eventPayload } : {}),
                }
              : seg
          ),
        }));
        break;
      }

      case 'text_delta': {
        const idx = currentTextIndexRef.current;
        if (idx === null) {
          setState(prev => {
            const next = [...prev.liveSegments, { type: 'text' as const, text: event.delta as string }];
            currentTextIndexRef.current = next.length - 1;
            return { ...prev, liveSegments: next };
          });
        } else {
          setState(prev => {
            const segs = [...prev.liveSegments];
            const seg = segs[idx];
            if (seg?.type === 'text') {
              segs[idx] = { ...seg, text: seg.text + (event.delta as string) };
            }
            return { ...prev, liveSegments: segs };
          });
        }
        break;
      }

      case 'artifact_created':
        setState(prev => ({
          ...prev,
          latestArtifact: {
            artifactId: event.artifactId as string,
            artifactGroupId: event.artifactGroupId as string,
            version: event.version as number,
            fileType: event.fileType as string,
            title: event.title as string,
            filename: event.filename as string,
            fileSizeBytes: event.fileSizeBytes as number,
          },
        }));
        break;

      case 'complete':
        currentTextIndexRef.current = null;
        setState(prev => ({
          ...prev,
          isStreaming: false,
          lastStopReason: (event.stopReason as string | undefined) ?? null,
        }));
        onCompleteRef.current?.();
        break;

      case 'error':
        currentTextIndexRef.current = null;
        setState(prev => ({
          ...prev,
          isStreaming: false,
          error: event.error as string,
        }));
        break;
    }
  }, [navigate]);

  const reset = useCallback(() => {
    currentTextIndexRef.current = null;
    setState(INITIAL_STATE);
  }, []);

  const startChat = useCallback(async (
    message: string,
    uploadIds?: string[],
    agentId?: string,
    modelOverride?: string,
    enableCacheDiagnostics?: boolean,
  ): Promise<void> => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    currentTextIndexRef.current = null;
    setState({ ...INITIAL_STATE, isStreaming: true });

    try {
      await v2ChatApi.startChat(
        {
          message,
          ...(uploadIds?.length ? { upload_ids: uploadIds } : {}),
          ...(agentId ? { agent_id: agentId } : {}),
          ...(modelOverride ? { model_override: modelOverride } : {}),
          ...(enableCacheDiagnostics ? { enable_cache_diagnostics: true } : {}),
        },
        handleEvent,
        controller.signal,
      );
    } catch (err: unknown) {
      if (err instanceof Error && err.name === 'AbortError') return;
      const msg = err instanceof Error ? err.message : 'Failed to start chat';
      setState(prev => ({ ...prev, isStreaming: false, error: msg }));
      throw err;
    }
  }, [handleEvent]);

  const sendMessage = useCallback(async (
    threadId: string,
    message: string,
    uploadIds?: string[],
  ): Promise<void> => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    currentTextIndexRef.current = null;
    setState({ ...INITIAL_STATE, isStreaming: true });

    try {
      await v2ChatApi.sendMessage(
        { thread_id: threadId, message, ...(uploadIds?.length ? { upload_ids: uploadIds } : {}) },
        handleEvent,
        controller.signal,
      );
    } catch (err: unknown) {
      if (err instanceof Error && err.name === 'AbortError') return;
      const msg = err instanceof Error ? err.message : 'Failed to send message';
      setState(prev => ({ ...prev, isStreaming: false, error: msg }));
      throw err;
    }
  }, [handleEvent]);

  const abort = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    currentTextIndexRef.current = null;
    setState(prev => ({ ...prev, isStreaming: false }));
  }, []);

  const abortStream = useCallback(async (threadId: string): Promise<void> => {
    // Guard: the hard-block contract (decision 6) means we await the backend
    // ack before unlocking the input. Flip isAborting up front so the UI
    // can reflect the pending state immediately.
    setState(prev => ({ ...prev, isAborting: true }));
    try {
      const result = await v2ChatApi.abortStream(threadId);
      // The SSE stream will also fire a `complete` event with the final
      // stopReason; `isStreaming` may already be false by the time we get
      // here. We still force it false in case the SSE write lost the race,
      // and record stop_reason for the visual indicator on the live bubble.
      setState(prev => ({
        ...prev,
        isStreaming: false,
        isAborting: false,
        lastStopReason: result.stop_reason ?? prev.lastStopReason ?? 'aborted',
      }));
    } catch (err) {
      // Fallback: don't leave the user stranded with a locked input.
      // Client-side abort as a last resort. This does not stop the backend,
      // but at least releases the UI; the backend will eventually either
      // finalize on its own or hit maxTurns.
      abortRef.current?.abort();
      abortRef.current = null;
      currentTextIndexRef.current = null;
      const msg = err instanceof Error ? err.message : 'Failed to stop stream';
      setState(prev => ({
        ...prev,
        isStreaming: false,
        isAborting: false,
        error: msg,
      }));
    }
  }, []);

  return (
    <StreamingChatContext.Provider
      value={{
        ...state,
        startChat,
        sendMessage,
        abort,
        abortStream,
        reset,
      }}
    >
      {children}
    </StreamingChatContext.Provider>
  );
};
